import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DARK_MAP_STYLE } from '@/components/Map/Utils/mapStyleTypes';
import {
  TabbedLegendProvider,
  useTabbedLegendContext,
} from '../../components/Legend/tabbedLegend.context';
import MapInstance from '../../components/Map';
import { DEFAULT_LEGEND_ITEMS, WELL_LEGEND_ITEM_INDEX } from '../../components/Map/Utils/constants';
import {
  getUserPreferenceMapStyle,
  setUserPreferenceMapStyle,
} from '../../components/Map/Utils/mapStyles';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { getFulltextResultParameters } from '../../utils/globalSearch';
import { getLatLongFromPoint } from '../../utils/helpers';
import { LAYERS } from './constants';
import Filters from './Filters';
import MapFilterContext, { MapFilterProvider } from './Filters/filterContext';
import Info from './Info';
import { ScreenshotMode } from './ScreenshotMode';

const useStyles = makeStyles(theme => ({
  root: {
    ...theme.sizes.fullPage,
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'hidden',
  },
  filterButton: {
    position: 'absolute',
    marginRight: theme.spacing(2),
    left: '25px',
    top: '10px',
    ...theme.palette.actionCustom,
  },
  filterButtonShift: {
    position: 'absolute',
    marginRight: theme.spacing(2),
    left: '315px',
    top: '10px',
    ...theme.palette.actionCustom,
    [theme.breakpoints.down('md')]: {
      left: '240px',
    },
  },
  infoButton: {
    position: 'absolute',
    marginRight: theme.spacing(2),
    right: '400px',
    top: '10px',
    zIndex: 2,
    transition: 'width 0.4s ease-in-out',
    ...theme.palette.actionCustom,
    [theme.breakpoints.down('lg')]: {
      right: '325px',
    },
  },
  infoButtonWider: {
    right: '905px',
    [theme.breakpoints.down('lg')]: {
      right: '725px',
    },
    [theme.breakpoints.down(1120)]: {
      right: '525px',
    },
    [theme.breakpoints.down(800)]: {
      right: '350px',
    },
  },
  screenshotModeButton: {
    position: 'absolute',
    marginRight: theme.spacing(2),
    right: '10px',
    top: '10px',
    zIndex: 4,
    ...theme.palette.actionCustom,
    [theme.breakpoints.down('lg')]: {
      right: '325px',
    },
  },
  screenshot: {
    maxWidth: '100%',
    height: 'auto',
    padding: '10px',
  },
  imageContainerStyle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const OilAndGasMapPage = () => {
  const { tabChosen, setTabChosen } = useTabbedLegendContext();
  const classes = useStyles();
  const [showParticipationHistory, setShowParticipationHistory] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isScreenshotModeOpen, setIsScreenshotModeOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [map, setMap] = useState(null);
  const { inDevelopment, mapboxPoc } = useFeatureFlags();
  const [style, setStyle] = useState(
    inDevelopment ? DARK_MAP_STYLE.value : getUserPreferenceMapStyle(DARK_MAP_STYLE.value),
  );
  // keep track of prevItem in order to remove highlight
  const prevItemRef = useRef();

  const { variables, secondaryLegendValues } = useContext(MapFilterContext);

  const showSecondaryLegend = variables.company.length > 0;

  const toggleFilterDrawer = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const toggleInfoDrawer = () => {
    setIsInfoOpen(!isInfoOpen);
  };

  const toggleScreenshotModeDrawer = useCallback(() => {
    setIsScreenshotModeOpen(v => !v);
    setIsFilterOpen(v => !v);
  }, []);

  // Handle search params
  // Handle all entity types from global search, not just BASIN
  const { entity, geom, name, isoCode } = getFulltextResultParameters();

  // Calculate map position and zoom based on global search parameters
  const { latLong, defaultMapPosition } = useMemo(() => {
    if (entity && geom) {
      const calculatedLatLong = getLatLongFromPoint(geom);

      if (!calculatedLatLong) {
        return { latLong: null, defaultMapPosition: null };
      }

      // Set appropriate zoom levels for different entity types
      let zoom = 4; // default zoom
      switch (entity) {
        case 'BASIN':
          zoom = 4;
          break;
        case 'COUNTRY':
          zoom = 4;
          break;
        case 'ASSET':
        case 'BLOCK':
        case 'LEASE':
          zoom = isoCode !== 'USA' ? 9 : 13;
          break;
        case 'FIELD':
          zoom = 9;
          break;
        case 'WELL':
          zoom = 13;
          break;
        case 'FACILITY':
        case 'LNG_PROJECT':
          zoom = 10;
          break;
        case 'PIPELINE_NETWORK':
          zoom = 6;
          break;
        default:
          zoom = 8;
      }

      return {
        latLong: calculatedLatLong,
        defaultMapPosition: { latLong: `[${calculatedLatLong}]`, zoom },
      };
    }
    return { latLong: null, defaultMapPosition: null };
  }, [entity, geom, isoCode]);

  // Keeping this in useEffect ensures this only gets called once
  // biome-ignore lint/correctness/useExhaustiveDependencies(name): suppress dependency name
  // biome-ignore lint/correctness/useExhaustiveDependencies(entity): suppress dependency entity
  // biome-ignore lint/correctness/useExhaustiveDependencies(latLong[1]): suppress dependency latLong[1]
  // biome-ignore lint/correctness/useExhaustiveDependencies(toggleInfoDrawer): suppress dependency toggleInfoDrawer
  // biome-ignore lint/correctness/useExhaustiveDependencies(latLong[0]): suppress dependency latLong[1]
  useEffect(() => {
    if (entity === 'BASIN') {
      setCurrentItem({
        coordinates: { lat: latLong[1], lng: latLong[0] },
        layerCountry: null,
        properties: { name, id: null },
        sourceLayer: 'basins',
      });
      toggleInfoDrawer();
    }
  }, []);

  // Highlight layer
  useEffect(() => {
    if (!map) return; // Guard: do nothing if map is not ready
    const prevItem = prevItemRef.current;
    let layer = currentItem ? `${currentItem.layerCountry}_${currentItem.sourceLayer}` : null;
    layer = mapboxPoc && currentItem ? currentItem.sourceLayer : layer;
    if (prevItem !== null && prevItem !== undefined) {
      map.removeLayerHighlight(prevItem, layer);
    }
    if (currentItem !== null) {
      map.addLayerHighlight(currentItem, prevItemRef, layer);
    }
  }, [currentItem, map, mapboxPoc]);

  // Ensure map flies to correct position when global search parameters are present and map is loaded
  useEffect(() => {
    if (map && entity && geom && latLong && defaultMapPosition) {
      // Wait for map to be fully loaded before flying to position
      if (map.isStyleLoaded()) {
        map.flyTo({
          center: latLong,
          zoom: defaultMapPosition.zoom,
        });
      } else {
        // If map style isn't loaded yet, wait for it
        map.once('style.load', () => {
          map.flyTo({
            center: latLong,
            zoom: defaultMapPosition.zoom,
          });
        });
      }
    }
  }, [map, entity, geom, latLong, defaultMapPosition]);

  // Remove highlight when info panel is closed:
  // biome-ignore lint/correctness/useExhaustiveDependencies(map.removeLayerHighlight): suppress dependency map.removeLayerHighlight
  useEffect(() => {
    const prevItem = prevItemRef.current;
    if (!isInfoOpen && !!prevItem) {
      map.removeLayerHighlight(prevItem, null);
    }
  }, [isInfoOpen]);

  const layerClickCallback = newItem => {
    toggleInfoDrawer();
    setCurrentItem(newItem);
  };

  const toggleMapStyle = event => {
    const newStyle = event.target.value;
    setUserPreferenceMapStyle(newStyle);
    setStyle(newStyle);
    map.toggleMapStyle(newStyle);
  };
  const participantLegendShown = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies(tabChosen): suppress dependency tabChosen
  // biome-ignore lint/correctness/useExhaustiveDependencies(participantLegendShown.current): suppress dependency participantLegendShown.current
  // biome-ignore lint/correctness/useExhaustiveDependencies(setTabChosen): suppress dependency setTabChosen
  const tabbedItems = useMemo(() => {
    if (showSecondaryLegend && !!secondaryLegendValues) {
      const legendItemsNew = [
        ...DEFAULT_LEGEND_ITEMS,
        {
          label: 'Company',
          items: secondaryLegendValues,
        },
      ];
      if (participantLegendShown.current) {
        setTabChosen(legendItemsNew.length - 1);
      }
      participantLegendShown.current = true;
      return legendItemsNew;
    }
    participantLegendShown.current = false;
    if (tabChosen > DEFAULT_LEGEND_ITEMS.length - 1) {
      setTabChosen(DEFAULT_LEGEND_ITEMS.length - 1);
    }
    return DEFAULT_LEGEND_ITEMS;
  }, [showSecondaryLegend, secondaryLegendValues, participantLegendShown.current]);

  return (
    <div className={classes.root}>
      <MapInstance
        map={map}
        setMap={setMap}
        style={style}
        setStyle={setStyle}
        legend
        ruler
        control
        enableEntityHighlight
        layerClickCallback={layerClickCallback}
        layers={LAYERS}
        legendProps={{
          enabled: true,
          tabbed: true,
          tabbedItems: tabbedItems,
        }}
        defaultMapPosition={defaultMapPosition}
        countryClickProps={{
          enabled: false,
          filters: {},
          selectedCountry: {},
        }}
      />
      <Filters
        isOpen={isFilterOpen}
        style={style}
        toggleMapStyle={toggleMapStyle}
        map={map}
        setCurrentItem={setCurrentItem}
        setIsInfoOpen={setIsInfoOpen}
        shrink={showSecondaryLegend}
      />
      <Info
        isOpen={isInfoOpen}
        item={currentItem?.properties || null}
        layerName={currentItem?.sourceLayer || null}
        layerCountry={currentItem?.layerCountry || null}
        map={map}
        prevItemRef={prevItemRef}
        toggleScreenshotModeDrawer={toggleScreenshotModeDrawer}
        showParticipationHistory={showParticipationHistory}
        setShowParticipationHistory={setShowParticipationHistory}
        shrink={showSecondaryLegend}
      />
      <ScreenshotMode
        isOpen={isScreenshotModeOpen}
        item={currentItem?.properties || null}
        map={map}
      />
      {/* biome-ignore lint/correctness/useUniqueElementIds: Required ID for screenshot feature */}
      <IconButton
        id='map-filter-button'
        aria-label='open filter drawer'
        onClick={toggleFilterDrawer}
        edge='start'
        className={isFilterOpen ? classes.filterButtonShift : classes.filterButton}
        size='large'
      >
        {isFilterOpen ? <CloseIcon /> : <FilterListIcon />}
      </IconButton>
      {isInfoOpen && (
        <IconButton
          aria-label='close info drawer'
          onClick={toggleInfoDrawer}
          edge='start'
          className={clsx(classes.infoButton, {
            [classes.infoButtonWider]: showParticipationHistory,
          })}
          size='large'
        >
          <CloseIcon />
        </IconButton>
      )}
      {isScreenshotModeOpen && (
        // biome-ignore lint/correctness/useUniqueElementIds: Required for screenshot feature
        <IconButton
          id='close-screenshot-button'
          aria-label='close screenshot drawer'
          onClick={toggleScreenshotModeDrawer}
          edge='start'
          className={classes.screenshotModeButton}
          size='large'
        >
          <CloseIcon />
        </IconButton>
      )}
    </div>
  );
};
const OilAndGasMap = () => (
  <MapFilterProvider>
    <TabbedLegendProvider initialTabChosen={WELL_LEGEND_ITEM_INDEX}>
      <OilAndGasMapPage />
    </TabbedLegendProvider>
  </MapFilterProvider>
);

export default OilAndGasMap;
